import keys from "lodash/keys"

import { SELECT, UPDATE, DELETE, CREATE, CLONE } from "redux/utility"
import { LOADING, LOADED, ERROR } from "redux/middleware/actionNames"

import { LOGOUT } from "redux/reducers/core/authentication/actionNames"
import { COMPANY } from "redux/reducers/entities/companies/actionNames"
import { DELETE_CONTENT } from "redux/reducers/entities/journeySteps/actionNames"
import { MESSAGE } from "redux/reducers/entities/messages/actionNames"
import { TODO } from "redux/reducers/entities/todo/actionNames"

import {
    createPrerequisite,
    distributeState,
    updatePrerequisite,
    deletePrerequisite,
} from "redux/reducers/entities/prerequisites/prerequisitesClone/helpers"
import { deletePrerequisites } from "./helpers"
import { PREREQUISITE, PREREQUISITES } from "./actionNames"
import { JOURNEY } from "../journeys/actionNames"
import normalize from "./schema"
import { selectData, selectIdentifiers, selectClone } from "./selectors"

export const prerequisiteCloneInitialState = {
    /* Holds a clone of all the prerequisites */
    data: {},
    /* Holds a the identifiers of all the cloned prerequisites */
    identifiers: [],
}

export const initialState = {
    /* Holds the normalized prerequisites */
    data: {},
    /* Holds the normalized prerequisites identifiers */
    identifiers: [],
    /* Indicates whether or not prerequisites have been fetched */
    hasFetched: false,
    /* Indicates whether actions are working on a prerequisite */
    isLoading: false,
    /* Holds an error if the last action was erroneous  */
    loadingError: null,
    /* Holds the prerequisite that is currently selected */
    selectedHash: null,
    /* Holds the prerequisite state clone used during the republish */
    clone: prerequisiteCloneInitialState,
}

export const reducers = {
    [PREREQUISITES + LOADING](state) {
        return { ...state, isLoading: true, loadingError: null }
    },

    [PREREQUISITES + LOADED](state, { result }) {
        const [identifiers, data] = normalize(result)

        return {
            ...state,
            isLoading: false,
            hasFetched: true,
            identifiers,
            data,
        }
    },

    [PREREQUISITES + ERROR](state, { result: loadingError }) {
        return {
            ...state,
            isLoading: false,
            hasFetched: true,
            loadingError,
        }
    },

    [PREREQUISITE + CREATE + LOADING](state) {
        return { ...state, isLoading: true, loadingError: null }
    },

    [PREREQUISITE + CREATE + LOADED]: createPrerequisite,
    [PREREQUISITE + CREATE]: distributeState(createPrerequisite),

    [PREREQUISITE + CREATE + ERROR](state, { result: loadingError }) {
        return { ...state, isLoading: false, loadingError }
    },

    [PREREQUISITE + UPDATE + LOADING](state) {
        return { ...state, isLoading: true, loadingError: null }
    },

    [PREREQUISITE + UPDATE + LOADED]: updatePrerequisite,
    [PREREQUISITE + UPDATE]: distributeState(updatePrerequisite),

    [PREREQUISITE + UPDATE + ERROR](state, { result: loadingError }) {
        return { ...state, isLoading: false, loadingError }
    },

    [PREREQUISITE + DELETE + LOADING](state) {
        return { ...state, isLoading: true, loadingError: null }
    },

    [PREREQUISITE + DELETE + LOADED]: deletePrerequisite,
    [PREREQUISITE + DELETE]: distributeState(deletePrerequisite),

    [PREREQUISITE + DELETE + ERROR](state, { result: loadingError }) {
        return { ...state, isLoading: false, loadingError }
    },

    [PREREQUISITE + SELECT](state, { selectedHash }) {
        return { ...state, selectedHash }
    },

    // journey edit related actions

    [DELETE_CONTENT](state, { originalPayload }) {
        const { prerequisites } = originalPayload

        if (!prerequisites) return state

        const clone = selectClone(state)
        const data = deletePrerequisites(clone?.data, prerequisites)

        return {
            ...state,
            clone: {
                ...clone,
                data,
                identifiers: keys(data),
            },
        }
    },

    [DELETE_CONTENT + LOADED](state, { originalPayload }) {
        const { prerequisites } = originalPayload

        if (!prerequisites) return state

        const oldData = selectData(state)
        const data = deletePrerequisites(oldData, prerequisites)

        return {
            ...state,
            data,
            identifiers: keys(data),
        }
    },

    [MESSAGE + DELETE](state, { originalPayload }) {
        const { prerequisites } = originalPayload

        if (!prerequisites) return state

        const clone = selectClone(state)
        const data = deletePrerequisites(clone?.data, prerequisites)

        return {
            ...state,
            clone: {
                ...clone,
                data,
                identifiers: keys(data),
            },
        }
    },

    [MESSAGE + DELETE + LOADED](state, { originalPayload }) {
        const { prerequisites } = originalPayload

        if (!prerequisites) return state

        const oldData = selectData(state)
        const data = deletePrerequisites(oldData, prerequisites)

        return {
            ...state,
            data,
            identifiers: keys(data),
        }
    },

    [TODO + DELETE](state, { originalPayload }) {
        const { prerequisites } = originalPayload

        if (!prerequisites) return state

        const clone = selectClone(state)
        const data = deletePrerequisites(clone?.data, prerequisites)

        return {
            ...state,
            clone: {
                ...clone,
                data,
                identifiers: keys(data),
            },
        }
    },

    [TODO + DELETE + LOADED](state, { originalPayload }) {
        const { prerequisites } = originalPayload

        if (!prerequisites) return state

        const oldData = selectData(state)
        const data = deletePrerequisites(oldData, prerequisites)

        return {
            ...state,
            data,
            identifiers: keys(data),
        }
    },

    // republish related reducers
    [JOURNEY + CLONE](state) {
        const data = selectData(state)
        const identifiers = selectIdentifiers(state)

        return {
            ...state,
            clone: {
                data,
                identifiers,
            },
        }
    },

    // reducer reset
    [JOURNEY + SELECT](state) {
        const clone = selectClone(state)
        return { ...initialState, clone }
    },

    [COMPANY + SELECT]() {
        return initialState
    },

    [LOGOUT + LOADED]() {
        return initialState
    },
}
